import { createAsyncThunk } from "@reduxjs/toolkit";
import { setAllPractices, setPracticeLoading } from "./practiceSlice";
import hostURL from "../../Common/constants";
import { setSelectedFips } from "../filterSlice";
import { transformFilteredPracticeData } from "../../utils/transformPracticeData";
import { fetchGeoCountiesByState } from "../geo/geoThunk";
import { stateCode } from "../../modules/FieldReport/utils/helperFunctions";

export const fetchAllPracticeDataByCounty = createAsyncThunk(
  "practice/fetchAndCombineByCounty",
  async (_, { dispatch, getState }) => {
    const filterState = getState().filter;
    const selectedStateName = filterState.selectedState;
    const selectedState =
      stateCode[selectedStateName] || selectedStateName.toUpperCase();

    const selectedCounty = filterState.selectedCounty;

    dispatch(setPracticeLoading(true));

    try {
      const response = await fetch(
        `${hostURL}api/statepractices/${selectedState}/${selectedCounty}`
      );
      const data = await response.json();
      const raw = data[0];

      if (raw?.fips) {
        dispatch(setSelectedFips(String(raw.fips).padStart(5, "0")));
      }

      const basePractices = raw?.assetdata
        ? JSON.parse(raw.assetdata.substring(1, raw.assetdata.length - 1))
        : [];

      // Ensure countyListData is populated
      let geoState = getState().geo;
      let countyListData = geoState.countyListData || [];

      if (!countyListData.length) {
        await dispatch(fetchGeoCountiesByState(selectedState));
        geoState = getState().geo; // re-read after dispatch
        countyListData = geoState.countyListData || [];
      }

      const matchedCounty = countyListData.find(
        (item) => item.cname === selectedCounty
      );

      if (!matchedCounty) {
        console.warn(
          "⚠️ matchedCounty is null — countyListData might not be ready",
          {
            countyListData,
            selectedCounty,
          }
        );
      }

      const commonPracticeCodes = matchedCounty?.mostcommon
        ? matchedCounty.mostcommon
            .split(",")
            .map((x) => x.trim().toUpperCase())
            .filter(Boolean)
        : [];

      //FIRST PASS: collect all CREP data into a map keyed by pnumber
      const crepInfoMap = {};

      for (const practice of basePractices) {
        const prTitle = practice.title;
        const prName = prTitle?.substring(prTitle.indexOf("-") + 1)?.trim();
        if (!prName) continue;

        const crepRes = await fetch(
          `${hostURL}api/creppractices/${selectedState}/${encodeURIComponent(
            prName
          )}`
        );
        const crepDataRaw = await crepRes.text();

        let crepData = [];
        try {
          crepData = crepDataRaw ? JSON.parse(crepDataRaw) : [];
        } catch (e) {
          console.warn("❌ CREP parse error:", prName, e);
        }

        for (const entry of crepData) {
          const cpCode = entry.pnumber?.trim().toUpperCase();
          if (!cpCode) continue;

          if (!crepInfoMap[cpCode]) {
            crepInfoMap[cpCode] = [];
          }

          if (!crepInfoMap[cpCode].some((e) => e.id === entry.id)) {
            crepInfoMap[cpCode].push(entry);
          }
        }
      }

      // SECOND PASS: enrich each practice with nat/crp + matching crepInfo
      const enrichedPractices = await Promise.all(
        basePractices.map(async (practice) => {
          const prTitle = practice.title;
          const prCode = prTitle?.split("-")[0]?.trim();
          const prName = prTitle?.substring(prTitle.indexOf("-") + 1)?.trim();

          try {
            const [crpRes, natRes] = await Promise.all([
              fetch(
                `${hostURL}api/crppriorities/${prCode}/${encodeURIComponent(
                  prName
                )}`
              ),
              fetch(
                `${hostURL}api/natpractices/${prCode}/${encodeURIComponent(
                  prName
                )}`
              ),
            ]);

            const [crpDataRaw, natDataRaw] = await Promise.all([
              crpRes.text(),
              natRes.text(),
            ]);

            let crpData = {};
            let natData = {};
            try {
              crpData = crpDataRaw ? JSON.parse(crpDataRaw) : {};
            } catch (e) {
              console.warn("❌ CRP parse error:", prCode, e);
            }

            try {
              natData = natDataRaw ? JSON.parse(natDataRaw) : {};
            } catch (e) {
              console.warn("❌ NAT parse error:", prCode, e);
            }

            let parsedPriorityRating = {};
            try {
              parsedPriorityRating =
                natData.priorityrating &&
                typeof natData.priorityrating === "string"
                  ? JSON.parse(natData.priorityrating)
                  : natData.priorityrating;
            } catch (e) {
              console.warn("❌ priorityrating parse error:", prCode, e);
            }

            const matchingCREPs = crepInfoMap[prCode?.toUpperCase()] || [];

            const enriched = {
              ...practice,
              ...natData,
              ...crpData,
              ...(matchingCREPs.length > 0 ? { crepInfo: matchingCREPs } : {}),
              priorityrating: parsedPriorityRating,
              isMostCommon: commonPracticeCodes.includes(prCode?.toUpperCase()),
            };

            return enriched;
          } catch (err) {
            console.warn("❌ Metadata fetch failed for:", prCode, prName, err);
            return null;
          }
        })
      );

      const validCombined = enrichedPractices.filter(Boolean);
      const withoutStandaloneCREP = validCombined.filter(
        (p) => !p.title?.toUpperCase().startsWith("CREP -")
      );

      const finalTransformed = withoutStandaloneCREP.map(
        transformFilteredPracticeData
      );
      dispatch(setAllPractices(finalTransformed));
    } catch (err) {
      console.error("❌ Thunk error:", err);
    }

    dispatch(setPracticeLoading(false));
  }
);
